/* eslint-disable max-lines */
/* eslint-disable max-len */
import { ADDED_PRODUCT_POPUP_ID } from 'Component/AddToCartPopUp/AddToCartPopUp.config';
import { EXCLUDED_PAYNMENT_METHODS } from 'Component/CheckoutPayments/CheckoutPayments.config';
import CartQuery from 'Query/Cart.query';
import CartTotalsQuery from 'Query/CartTotals.query';
import CheckoutQuery from 'Query/Checkout.query';
import {
    CartDispatcher as SourceCartDispatcher
} from 'SourceStore/Cart/Cart.dispatcher';
import { setCartLoading, updateTotals } from 'Store/Cart/Cart.action';
import { countries } from 'Store/Config/Config.reducer';
import { showNotification } from 'Store/Notification/Notification.action';
import { showPopup as showAddedToCartPopup } from 'Store/Popup/Popup.action';
import { isSignedIn } from 'Util/Auth';
import BrowserDatabase from 'Util/BrowserDatabase';
import { getGuestQuoteId, setGuestQuoteId } from 'Util/Cart';
import componentLoader from 'Util/componentLoader';
import { fetchMutation, fetchQuery, getErrorMessage } from 'Util/Request';

import { updateShippingOptions } from './Cart.action';
import { getInitialState } from './Cart.reducer';

export const LinkedProductsDispatcher = componentLoader(() => import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */
    'Store/LinkedProducts/LinkedProducts.dispatcher'
), 2);
export const ConfigDispatcher = componentLoader(() => import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */
    'Store/Config/Config.dispatcher'
), 2);

/** @namespace Bodypwa/Store/Cart/Dispatcher */
export class CartDispatcher extends SourceCartDispatcher {
    abortController = null;

    async updateInitialCartData(dispatch) {
        if (this.abortController) {
            // Abort the previous request
            this.abortController.abort();
        }
        // Check if there is an ongoing request
        // Create a new AbortController
        this.abortController = new AbortController();

        // Create a signal from the AbortController
        const { signal } = this.abortController;

        try {
            dispatch(setCartLoading(false));

            const quoteId = await this._getGuestQuoteId(dispatch);
            const { cartData = {} } = await fetchQuery(
                CartQuery.getCartQuery(quoteId),
                signal // Pass the signal to the fetch request
            );

            // Reset the abortController variable since the request has completed
            this.abortController = null;

            return this._updateCartData(cartData, dispatch);
        } catch (error) {
            const { name } = error || {};
            // ... Error handling code ...
            if (name === 'AbortError') {
                // Handle cancellation, if desired
                return null;
            }
            // Reset the abortController variable since the request has completed
            this.abortController = null;

            return this.createGuestEmptyCart(dispatch);
        }
    }

    async createGuestEmptyCart(dispatch) {
        try {
            const {
                createEmptyCart: quoteId = ''
            } = await fetchMutation(CartQuery.getCreateEmptyCartMutation());

            setGuestQuoteId(quoteId);
            this._updateCartData({ items_qty: 0, items: [] }, dispatch);

            return quoteId;
        } catch (error) {
            dispatch(showNotification('error', getErrorMessage(error)));

            return null;
        }
    }

    async _updateCartData(cartData, dispatch) {
        dispatch(updateTotals(cartData));
        if (cartData.items && cartData.items.length && !cartData.shippingOptions) {
            this.updateShippingtData(cartData, dispatch);
        }
    }

    async updateShippingtData(cartData, dispatch) {
        const guestQuoteId = getGuestQuoteId();
        const country_id = this._getCountry();
        if (!BrowserDatabase.getItem('config')) {
            await ConfigDispatcher.then(
                ({ default: dispatcher }) => dispatcher.handleData(dispatch)
            );
        }
        const { shipping_method } = cartData;
        const address = {
            country_id
        };
        const { estimateShippingCosts } = await fetchMutation(CheckoutQuery.getEstimateShippingCosts(
            address,
            guestQuoteId
        ));

        const shippingOptions = shipping_method ? estimateShippingCosts.filter((option) => (shipping_method.includes(option.method_code)))[0] : estimateShippingCosts.reduce((prev, curr) => (prev.base_amount < curr.base_amount ? prev : curr));
        const addressInformation = this._getAddressInformation(shippingOptions);
        const { saveAddressInformation: totals } = await fetchMutation(CheckoutQuery.getSaveAddressInformation(
            addressInformation,
            guestQuoteId
        ));

        const data = this._getCartTotals(totals, cartData);
        dispatch(updateShippingOptions(estimateShippingCosts));
        dispatch(updateTotals(data));
    }

    async updateCartShippingtData(dispatch, cartData, shippingData) {
        const guestQuoteId = getGuestQuoteId();
        const addressInformation = this._getAddressInformation(shippingData);

        const { saveAddressInformation: totals } = await fetchMutation(CheckoutQuery.getSaveAddressInformation(
            addressInformation,
            guestQuoteId
        ));
        const { payment_methods } = totals;
        const cleanPaymentMethods = payment_methods.filter((item) => !EXCLUDED_PAYNMENT_METHODS.includes(item.code));
        const { shipping_carrier_code, shipping_method_code } = addressInformation;
        const data = this._getCartTotals({ ...totals, payment_methods: cleanPaymentMethods }, cartData, `${shipping_carrier_code}_${shipping_method_code}`);
        dispatch(updateTotals(data));
    }

    _getCountry() {
        const { countries: dataCountries } = BrowserDatabase.getItem('config') || {};
        const { countryId, country_id } = BrowserDatabase.getItem('CUSTOMER_ADDRESS') || {};
        if (countryId || country_id) {
            return countryId || country_id;
        }

        return countries[0] ? countries[0].id : dataCountries[0].id;
    }

    _getAddressInformation(shippingData = {}) {
        const country_id = this._getCountry();
        const { method_code = '', carrier_code = '' } = shippingData;
        const addressOptions = {
            company: null,
            country_id
        };

        return {
            shipping_carrier_code: carrier_code,
            shipping_method_code: method_code,
            shipping_address: addressOptions,
            billing_address: addressOptions
        };
    }

    _getCartTotals(totals, cartData, shippingMethod = '') {
        const {
            totals: {
                shipping_tax_amount, shipping_incl_tax, shipping_amount, base_grand_total, grand_total
            }
        } = totals;

        return {
            ...cartData, ...totals, shipping_tax_amount, shipping_incl_tax, shipping_amount, base_grand_total, grand_total, shipping_method: shippingMethod || cartData.shipping_method
        };
    }

    async applyCouponToCart(dispatch, couponCode) {
        try {
            const isCustomerSignedIn = isSignedIn();
            const guestQuoteId = !isCustomerSignedIn && getGuestQuoteId();

            if (!isCustomerSignedIn && !guestQuoteId) {
                return;
            }

            const { applyCoupon: { cartData = {} } = {} } = await fetchMutation(
                CartQuery.getApplyCouponMutation(couponCode, guestQuoteId)
            );

            this._updateCartData(cartData, dispatch);
            dispatch(showNotification('success', __('Coupon was applied!')));
        } catch (error) {
            dispatch(showNotification('error', __(getErrorMessage(error))));
        }
    }

    async changeItemQty(dispatch, options) {
        const { uid, quantity = 1, cartId: originalCartId } = options;

        const cartId = !originalCartId ? getGuestQuoteId() : originalCartId;

        try {
            if (!cartId) {
                return Promise.reject();
            }

            if (this.abortController) {
                // Abort the previous request
                this.abortController.abort();
            }

            await fetchMutation(
                CartQuery.getUpdateCartItemsMutation({
                    cart_id: cartId,
                    cart_items: [
                        {
                            cart_item_uid: uid,
                            quantity
                        }
                    ]
                })
            );

            this.updateLocalStorageCartData({
                cartId, quantity, uid, dispatch
            });

            return this.updateInitialCartData(dispatch);
        } catch (error) {
            dispatch(showNotification('error', getErrorMessage(error)));

            return Promise.reject();
        }
    }

    async addProductToCart(dispatch, options = {}) {
        const {
            products = [], cartId: userCartId, cartProduct, product
        } = options;

        const cartId = userCartId || getGuestQuoteId();

        if (this.abortController) {
            // Abort the previous request
            this.abortController.abort();
        }
        if (!Array.isArray(products) || products.length === 0) {
            dispatch(showNotification('error', __('No product data!')));
            return Promise.reject();
        }

        try {
            if (!cartId) {
                return Promise.reject();
            }

            const { addProductsToCart: { user_errors: errors = [] } = {} } = await fetchMutation(
                CartQuery.getAddProductToCartMutation(cartId, products)
            );

            if (Array.isArray(errors) && errors.length > 0) {
                errors.forEach((error) => {
                    dispatch(showNotification('error', getErrorMessage(error)));
                });

                return Promise.reject();
            }

            this.updateLocalStorageCartData({
                cartProduct, product, products, cartId, dispatch
            });

            dispatch(showAddedToCartPopup(ADDED_PRODUCT_POPUP_ID, { title: __('Added to cart') }));
            await this.updateInitialCartData(dispatch);
        } catch (error) {
            const { message = '' } = error[0] || {};
            const regex = /The current user cannot perform operations on cart "([^"]+)"/;
            if (regex.test(message)) {
                dispatch(showNotification('error', __('Your session is over, you are logged out!')));
                await this.createGuestEmptyCartCustom(dispatch);
                this.addProductToCart(dispatch, options);
            } else {
                dispatch(showNotification('error', getErrorMessage(error)));
            }

            return Promise.reject();
        }

        return Promise.resolve();
    }

    async createGuestEmptyCartCustom(dispatch) {
        try {
            const {
                createEmptyCartGuest: quoteId = ''
            } = await fetchMutation(CartQuery.getCreateEmptyCartGuestMutation());

            setGuestQuoteId(quoteId);
            this._updateCartData({}, dispatch);
            return quoteId;
        } catch (error) {
            dispatch(showNotification('error', getErrorMessage(error)));
            return null;
        }
    }

    updateCrossSellProducts(items, dispatch) {
        if (items && items.length) {
            const product_links = items.reduce((links, product) => {
                const { product: { product_links, variants = [] }, sku: variantSku } = product;

                const { product_links: childProductLinks } = variants.find(({ sku }) => sku === variantSku) || {};

                if (childProductLinks) {
                    Object.values(childProductLinks).filter(({ link_type }) => link_type === 'crosssell')
                        .map((item) => links.push(item));
                }

                if (product_links) {
                    Object.values(product_links).filter(({ link_type }) => link_type === 'crosssell')
                        .map((item) => links.push(item));
                }

                return links;
            }, []);

            if (product_links.length !== 0) {
                LinkedProductsDispatcher.then(
                    ({ default: dispatcher }) => dispatcher.fetchCrossSellProducts(dispatch, product_links)
                );
            } else {
                LinkedProductsDispatcher.then(
                    ({ default: dispatcher }) => dispatcher.clearCrossSellProducts(dispatch)
                );
            }
        } else {
            LinkedProductsDispatcher.then(
                ({ default: dispatcher }) => dispatcher.clearCrossSellProducts(dispatch)
            );
        }
    }

    resetGuestCart(dispatch) {
        return this._updateCartData({ items_qty: 0, items: [] }, dispatch);
    }

    async removeProductFromCart(dispatch, item_id) {
        try {
            const isCustomerSignedIn = isSignedIn();
            const guestQuoteId = !isCustomerSignedIn && getGuestQuoteId();

            if (!isCustomerSignedIn && !guestQuoteId) {
                return null;
            }
            dispatch(setCartLoading(true));
            const { removeCartItem: { cartData = {} } = {} } = await fetchMutation(
                CartQuery.getRemoveCartItemMutation(item_id, guestQuoteId)
            );

            this.updateLocalStorageCartData({ item_id, cartId: guestQuoteId, dispatch });
            if (this.abortController) {
                // Abort the previous request
                this.abortController.abort();
            }
            this._updateCartData(cartData, dispatch);
            return cartData;
        } catch (error) {
            dispatch(showNotification('error', getErrorMessage(error)));

            return null;
        }
    }

    _updateTotals(cartId, cartTotals, dispatch) {
        fetchQuery(
            CartTotalsQuery.getCartQuery(cartId),
        ).then(
            /** @namespace Bodypwa/Store/Cart/Dispatcher/CartDispatcher/_updateTotals/fetchQuery/then */
            ({ cartData }) => {
                dispatch(updateTotals({ ...cartTotals, ...cartData }));
            },
            /** @namespace Bodypwa/Store/Cart/Dispatcher/CartDispatcher/_updateTotals/fetchQuery/then/dispatch/catch */
            (error) => dispatch(showNotification('error', getErrorMessage(error)))
        );
    }

    updateLocalStorageCartData(cartData) {
        const {
            cartProduct, product, products, quantity, uid, item_id, dispatch, cartId, items_qty
        } = cartData;

        const { cartTotals: { items } = {}, cartTotals } = getInitialState();
        dispatch(setCartLoading(false));
        if (item_id) {
            // UPDATE CART ON REMOVE
            const updatedItems = items.filter((item) => item.item_id !== item_id);
            const qty = updatedItems.reduce((total, item) => total + item.qty, 0);
            dispatch(updateTotals({ ...cartTotals, items: updatedItems }));
            this._updateTotals(cartId, { ...cartTotals, items: updatedItems, items_qty: qty }, dispatch);
            return;
        }
        if (uid) {
            if (quantity === 0) {
                // UPDATE CART ON REMOVE WHEN qty = 0
                const updatedItems = items.filter((item) => btoa(item.item_id) !== uid);
                const qty = updatedItems.reduce((total, item) => total + item.qty, 0);
                dispatch(updateTotals({ ...cartTotals, items: updatedItems, items_qty: qty }));

                this._updateTotals(cartId, { ...cartTotals, items: updatedItems }, dispatch);
                return;
            }
            // UPDATE CART ON PLUS/MINUS
            const updatedItems = items.map((item) => {
                if (btoa(item.item_id) === uid) {
                    return {
                        ...item,
                        qty: quantity,
                        row_total: parseFloat((quantity * item.price).toFixed(2)),
                        row_total_incl_tax: parseFloat((quantity * item.price_incl_tax).toFixed(2))
                    };
                }

                return item;
            });

            const qty = updatedItems.reduce((total, item) => total + item.qty, 0);
            dispatch(updateTotals({ ...cartTotals, items: updatedItems, items_qty: qty }));
            this._updateTotals(cartId, { ...cartTotals, items: updatedItems }, dispatch);
            return;
        }

        // UPDATE CART ON ADD TO CART
        const {
            price_range: {
                minimum_price: {
                    final_price: { value: price }, discount: { amount_off: discount_amount, percent_off: discount_percent },
                    final_price_excl_tax: { value: priceExclTax }
                }
            }
        } = cartProduct;

        const updatedItems = items.map((item) => {
            if (item.sku === cartProduct.sku) {
                return {
                    ...item,
                    qty: item.qty + products[0].quantity,
                    row_total: parseFloat((priceExclTax * (item.qty + products[0].quantity)).toFixed(2)),
                    row_total_incl_tax: parseFloat((price * (item.qty + products[0].quantity)).toFixed(2))
                };
            }

            return item;
        });

        if (!updatedItems.some((item) => item.sku === cartProduct.sku)) {
            // UPDATE CART ON ADD TO CART AND ITEM DOES NOT EXIST
            const newItem = {
                qty: products[0].quantity,
                sku: cartProduct.sku,
                id: cartProduct.sku,
                product,
                customizable_options: [],
                downloadable_links: [],
                bundle_options: [],
                price,
                row_total: parseFloat((priceExclTax * products[0].quantity).toFixed(2)),
                row_total_incl_tax: parseFloat((price * products[0].quantity).toFixed(2)),
                tax_amount: 0,
                tax_percent: 0,
                discount_amount,
                discount_percent
            };

            updatedItems.push(newItem);
            dispatch(updateTotals({ ...cartTotals, items: updatedItems }));
            this._updateTotals(cartId, { ...cartTotals, items: updatedItems, items_qty: items_qty + products[0].quantity }, dispatch);
        }
    }
}

export default new CartDispatcher();
